import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Globe, X, Languages } from 'lucide-react';
import {
  SUPPORTED_LANGUAGES,
  detectBrowserLanguage,
  persistLanguage,
} from '../i18n';

const DISMISS_KEY = 'jobasti_public_translate_dismissed';
const ACCEPT_KEY = 'jobasti_public_translate_accepted';

/**
 * True for public job posting URLs like:
 *   /:slug/jobs
 *   /:slug/jobs/:id
 *   /:slug/jobs/:id/apply
 *   /:slug/about
 */
const isPublicJobRoute = (pathname) => {
  if (!pathname) return false;
  const segments = pathname.split('/').filter(Boolean);
  return segments.length >= 2 && (segments[1] === 'jobs' || segments[1] === 'about');
};

/**
 * A visitor-facing "Translate this page?" popup that appears ONLY on public
 * job posting pages when the visitor's browser language differs from the
 * page's source language (English by default).
 *
 * The page itself remains untranslated (recruiter's original wording).
 * On Accept, we fire Google Translate via persistLanguage(lang), which
 * translates the whole DOM to the visitor's language.
 */
const PublicJobTranslateBanner = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [target, setTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!isPublicJobRoute(location.pathname)) {
      setVisible(false);
      return () => { cancelled = true; };
    }

    const check = () => {
      try {
        if (localStorage.getItem(DISMISS_KEY) === '1') return;
        if (localStorage.getItem(ACCEPT_KEY) === '1') return;
      } catch { /* noop */ }

      // If the user has already picked a Google Translate target, skip.
      const goog = document.cookie.split('; ').find(c => c.startsWith('googtrans='));
      if (goog && goog.includes('/en/')) return;

      const browser = detectBrowserLanguage();
      if (!browser || browser === 'en') return; // page is EN; browser is EN → nothing to do

      if (cancelled) return;
      setTarget(browser);
      setVisible(true);
    };

    // Small delay so the page has time to render its own content first
    const id = setTimeout(check, 900);
    return () => { cancelled = true; clearTimeout(id); };
  }, [location.pathname]);

  if (!visible || !target) return null;
  const langMeta = SUPPORTED_LANGUAGES.find((l) => l.code === target);
  if (!langMeta) return null;

  const accept = () => {
    try { localStorage.setItem(ACCEPT_KEY, '1'); } catch { /* noop */ }
    persistLanguage(target);
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] max-w-lg w-[94%] bg-white border border-gray-200 rounded-2xl shadow-2xl px-5 py-4 flex items-center gap-4 animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="public-translate-banner"
      role="dialog"
      aria-label="Translate this page"
    >
      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
        <Globe className="w-5 h-5 text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] text-gray-900 font-semibold leading-tight">
          Translate this page to {langMeta.label}?
        </p>
        <p className="text-[11.5px] text-gray-500 mt-1 flex items-center gap-1">
          <Languages className="w-3 h-3" />
          Powered by Google Translate
        </p>
      </div>
      <button
        type="button"
        onClick={accept}
        className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-lg px-4 py-2 transition shadow-sm"
        data-testid="public-translate-accept"
      >
        <span className="text-base leading-none">{langMeta.flag}</span>
        {langMeta.label}
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="p-2 text-gray-400 hover:text-gray-600 rounded-full transition"
        aria-label="Dismiss"
        data-testid="public-translate-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PublicJobTranslateBanner;
